// useTypeFieldsResolvedMeta.js

import { useMemo } from 'react';

/**
 * useTypeFieldsResolvedMeta
 *
 * Resolves effective base/type keys and display label for type field mutations.
 *
 * @param {string} nodeTypeKey - Normalized node type key from component props.
 * @param {object|null} typeDef - Resolved node type definition.
 * @param {string|null} baseKey - Optional base key override.
 *
 * @returns {object} Resolved base key, type key, and display label.
 */
export default function useTypeFieldsResolvedMeta({
  nodeTypeKey,
  typeDef,
  baseKey,
}) {
  const resolvedBaseKey = useMemo(() => {
    if (typeof baseKey === 'string' && baseKey.trim()) return baseKey.trim();
    if (typeof typeDef?.baseKey === 'string' && typeDef.baseKey.trim()) return typeDef.baseKey.trim();
    return null;
  }, [typeDef?.baseKey, baseKey]);

  const resolvedTypeKey = useMemo(() => {
    const fromDef = typeDef?.key ? String(typeDef.key).trim() : '';
    if (fromDef) return fromDef;
    return nodeTypeKey;
  }, [nodeTypeKey, typeDef?.key]);

  const nodeTypeLabel = useMemo(() => {
    const fromDef = typeof typeDef?.label === 'string' ? typeDef.label.trim() : '';
    if (fromDef) return fromDef;
    return resolvedTypeKey || 'Type';
  }, [resolvedTypeKey, typeDef?.label]);

  return {
    resolvedBaseKey,
    resolvedTypeKey,
    nodeTypeLabel,
  };
}
